import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MessagesPendingReconcileHealth } from "./types";

export function MessagesPendingReconcileHealthPanel() {
  const { toast } = useToast();
  const healthQuery = useQuery<MessagesPendingReconcileHealth>({
    queryKey: ["/api/admin/messages-pending-reconcile-health"],
    queryFn: async () => {
      const res = await fetch("/api/admin/messages-pending-reconcile-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load messages_pending reconcile health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const [forceResolveId, setForceResolveId] = useState<number | null>(null);
  const [forceResolveInput, setForceResolveInput] = useState("");

  const forceReconcileMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/messages-pending/${id}/force-reconcile`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Force-reconcile failed");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Done", description: `Row matched to existing harvested chat (chat #${data.reconciledChatId}).` });
      healthQuery.refetch();
      setForceResolveId(null);
      setForceResolveInput("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const data = healthQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-messages-pending-reconcile-title">
            Messages Pending Reconciliation
          </h3>
          <p className="text-sm text-muted-foreground">
            Counts of <code>messages_pending</code> rows that have been sent (<code>sent_at IS NOT NULL</code>) but
            do not yet have a <code>reconciled_chat_id</code>. The reconcile cron only retries rows whose{" "}
            <code>sent_at</code> is within the last {data?.lookbackDays ?? 7} days — anything older is stuck and
            must be investigated. <strong>Stale-published</strong> rows were confirmed sent to PadSplit (attempts &gt; 0, no error)
            but the Lambda never stamped <code>sent_at</code>; the reconcile cron auto-recovers these after 30 min.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => healthQuery.refetch()}
          disabled={healthQuery.isFetching}
          data-testid="button-refresh-messages-pending-reconcile"
        >
          {healthQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {healthQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : healthQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-messages-pending-reconcile-error">
            {parseErrorMessage(healthQuery.error, "Failed to load messages_pending reconcile health")}
          </p>
        </Card>
      ) : data ? (
        <Card className="p-6" data-testid="card-messages-pending-reconcile-summary">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              {data.withinWindow === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <Clock className="h-5 w-5 text-yellow-600" />
              )}
              <span className="font-semibold tabular-nums" data-testid="text-messages-pending-reconcile-within-window">
                {data.withinWindow}
              </span>
              <span className="text-sm text-muted-foreground">
                in window (last {data.lookbackDays}d, reconciler will retry)
              </span>
            </div>
            <div className="flex items-center gap-2">
              {data.outsideWindow === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              )}
              <span className="font-semibold tabular-nums" data-testid="text-messages-pending-reconcile-outside-window">
                {data.outsideWindow}
              </span>
              <span className="text-sm text-muted-foreground">
                stuck (older than {data.lookbackDays}d, never retried)
              </span>
            </div>
            <div className="flex items-center gap-2">
              {(data.stalePublished ?? 0) === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <Clock className="h-5 w-5 text-orange-500" />
              )}
              <span className="font-semibold tabular-nums" data-testid="text-messages-pending-reconcile-stale-published">
                {data.stalePublished ?? 0}
              </span>
              <span className="text-sm text-muted-foreground">
                stale-published (sent_at missing &gt;30 min, auto-recovers)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Total unreconciled:</span>
              <span className="font-semibold tabular-nums" data-testid="text-messages-pending-reconcile-total">
                {data.total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Oldest sent_at:</span>
              <span className="text-sm tabular-nums" data-testid="text-messages-pending-reconcile-oldest">
                {data.oldestSentAt ? new Date(data.oldestSentAt).toLocaleString() : "—"}
              </span>
            </div>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="text-messages-pending-reconcile-generated-at">
              Updated {new Date(data.generatedAt).toLocaleTimeString()}
            </span>
          </div>
          {/* Task #2932 — Force-resolve a single stale pending row by ID */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium mb-2">Force-resolve a stuck row</p>
            <p className="text-xs text-muted-foreground mb-3">
              Enter the <code>messages_pending</code> row ID to immediately reconcile it (match to an existing
              chat or synthesize a new one). Use this to unblock a "Sending via PadSplit…" spinner without
              waiting for the next cron tick.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="border rounded px-2 py-1 text-sm w-32"
                placeholder="Row ID"
                value={forceResolveInput}
                onChange={(e) => setForceResolveInput(e.target.value)}
                data-testid="input-force-reconcile-id"
              />
              <Button
                size="sm"
                variant="destructive"
                disabled={!forceResolveInput.trim() || forceReconcileMutation.isPending}
                onClick={() => {
                  const id = parseInt(forceResolveInput.trim(), 10);
                  if (!isNaN(id)) forceReconcileMutation.mutate(id);
                }}
                data-testid="button-force-reconcile-submit"
              >
                {forceReconcileMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Force-resolve
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
